import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useLang } from '../../../hooks/useLang';
import { useScrollProgress } from '../../../hooks/useScrollProgress';

const preBlockchainTimeline = [
  { year: '1976', zh: '非对称加密技术诞生', en: 'Asymmetric Cryptography Born', detail: { zh: 'Whitfield Diffie 与 Martin Hellman 发表划时代论文，首次提出公钥与私钥的加密/解密逻辑，解决对称加密的密钥传输安全问题，为区块链和RWA底层安全奠定数学基础。', en: 'Diffie and Hellman published their landmark paper introducing public-key cryptography — the mathematical foundation of all blockchain security and RWA ownership verification.' }, tags: ['密码学基础', 'Diffie-Hellman'], milestone: true },
  { year: '1982', zh: '拜占庭将军问题提出', en: 'Byzantine Generals Problem', detail: { zh: 'Leslie Lamport 提出分布式网络中"节点不可信但需达成共识"的核心难题及解决方案，直接推动了区块链共识机制的研发，成为RWA大规模协作的理论基石。', en: 'Lamport proposed the fundamental problem of achieving consensus in distributed networks with unreliable nodes — the theoretical cornerstone for blockchain consensus and large-scale RWA cooperation.' }, tags: ['分布式系统', 'Lamport'], milestone: false },
  { year: '1982', zh: '盲签名技术提出', en: 'Blind Signature Technology', detail: { zh: 'David Chaum 提出盲签名技术，为数字货币隐私保护奠定理论基础，同年创办DigiCash，为后来比特币的诞生埋下思想种子。该技术后来成为RWA敏感资产匿名确权的重要技术源头。', en: 'David Chaum proposed blind signatures, laying the theoretical basis for digital currency privacy. His DigiCash company was the earliest digital cash prototype, planting the seeds for Bitcoin.' }, tags: ['David Chaum', 'eCash前身', '隐私技术'], milestone: false },
  { year: '1991', zh: '加密数字时间戳系统诞生', en: 'Cryptographic Timestamp System', detail: { zh: 'Stuart Haber 与 W. Scott Stornetta 首次提出基于密码学的数字时间戳系统，实现"数据一旦记录不可篡改"，这被公认为区块链技术的最早概念原型。中本聪在比特币白皮书中引用了他们的研究成果。', en: 'Haber & Stornetta proposed the first cryptographic timestamp system — making data tamper-proof. Widely recognized as the earliest conceptual prototype of blockchain. Cited directly in Satoshi\'s Bitcoin whitepaper.' }, tags: ['Haber & Stornetta', '区块链原型', '时间戳'], milestone: true },
  { year: '1994', zh: '智能合约概念提出', en: 'Smart Contract Concept', detail: { zh: 'Nick Szabo 首次提出智能合约概念：计算机代码可自动执行合约条款，无需第三方干预。这一思想成为RWA资产的自动分红、赎回、履约提供核心思想，是RWA产业化的关键前提。', en: 'Nick Szabo proposed smart contracts — code that automatically executes agreement terms without intermediaries. The foundational idea behind all RWA automation: automatic distributions, redemptions, and compliance.' }, tags: ['Nick Szabo', '智能合约', 'RWA思想源头'], milestone: true },
  { year: '1998', zh: 'Bit Gold设计——RWA思想雏形', en: 'Bit Gold Design — Proto-RWA', detail: { zh: 'Nick Szabo 设计"工作量证明+链上记录"的去中心化资产模型，首个完整的"实体价值锚定"数字资产模型，比特币直接从此发展而来。"合约化资产""链上履约"思想成为RWA资产代币化的思想先驱。', en: 'Nick Szabo\'s Bit Gold was the first complete "real-value-anchored" digital asset model — directly inspiring Bitcoin and establishing the foundational logic of "real asset → on-chain token."' }, tags: ['Bit Gold', 'Nick Szabo', 'RWA先驱'], milestone: true },
  { year: '2004', zh: '可重用工作量证明（RPoW）发明', en: 'Reusable Proof of Work (RPoW)', detail: { zh: 'Hal Finney 发明可重用工作量证明系统，解决数字货币的重复花费问题，成为比特币工作量证明机制的直接原型，为RWA资产的链上交易安全提供了技术验证。', en: 'Hal Finney invented the Reusable Proof of Work system, solving the double-spend problem in digital currency — the direct conceptual precursor to Bitcoin\'s PoW mechanism.' }, tags: ['Hal Finney', 'RPoW', '工作量证明'], milestone: false },
  { year: '2008', zh: '比特币白皮书发布——区块链正式诞生', en: 'Bitcoin Whitepaper — Blockchain Born', detail: { zh: '2008年10月31日，中本聪（Satoshi Nakamoto）匿名发表比特币白皮书，首次提出"区块链"技术概念，构建去中心化账本系统。2009年1月3日挖出创世区块，正式启动比特币网络，区块链技术从理论走向实践。', en: 'October 31, 2008: Satoshi Nakamoto published the Bitcoin whitepaper, introducing blockchain — a decentralized, tamper-proof ledger. January 3, 2009: the genesis block was mined, blockchain went live.' }, tags: ['中本聪', '比特币白皮书', '创世区块'], milestone: true },
];

const intlTimeline = [
  { year: '2009', zh: '比特币网络运行，全球第一笔链上转账', en: 'Bitcoin Network Launches, First On-Chain Transfer', detail: { zh: '2009年1月3日，比特币创世区块挖出。1月12日，中本聪向Hal Finney转账10 BTC，完成全球首笔链上资产转移，验证了去中心化账本的可行性。', en: 'January 3: Genesis block mined. January 12: First ever on-chain asset transfer — Satoshi sends 10 BTC to Hal Finney, validating the entire concept of a decentralized ledger.' }, tags: ['比特币', '创世区块', '链上交易'], milestone: true },
  { year: '2011', zh: '彩色币（Colored Coins）——第一个RWA原型', en: 'Colored Coins — First RWA Prototype', detail: { zh: '比特币社区提出"彩色币"协议，允许在比特币区块链上标记特定代币代表现实世界资产（股票、房产、黄金）。这是人类历史上第一个RWA原型，首次实现"单一区块链承载多类实体资产代币化"。', en: 'The Bitcoin community proposed "Colored Coins" — allowing BTC transactions to represent real-world assets (stocks, property, gold). The world\'s first RWA prototype: one blockchain, multiple asset types.' }, tags: ['彩色币', 'RWA原型', '资产代币化'], milestone: true },
  { year: '2012', zh: 'AsicMiner——中国RWA雏形', en: 'AsicMiner — China\'s RWA Pioneer', detail: { zh: '蒋信予（烤猫）在比特币网络上发行AsicMiner股票，将矿机公司未来收益拆分为链上代币，设计自动分红机制，每周向持有者分配收益。这一模式比以太坊智能合约还早三年，是中国RWA实践的重要里程碑。', en: 'Jiang Xinyu issued AsicMiner tokens on Bitcoin — splitting future mining profits into on-chain tokens with weekly automatic dividends. This predated Ethereum smart contracts by 3 years.' }, tags: ['AsicMiner', '烤猫', '中国RWA先驱'], milestone: false },
  { year: '2015', zh: 'Ethereum 主网上线', en: 'Ethereum Mainnet Launch', detail: { zh: '以太坊主网正式上线，智能合约功能使"在链上代表真实资产"成为技术可能。Vitalik Buterin 白皮书中首次描述可编程区块链的金融用途，RWA的复杂规则（分红、质押、赎回）成为技术可行。', en: 'Ethereum mainnet went live. Smart contracts made it technically possible to represent real assets on-chain with complex rules — dividends, staking, and redemption. The RWA era became technically possible.' }, tags: ['以太坊', '智能合约', 'Vitalik'], milestone: true },
  { year: '2016', zh: 'Chainlink成立，预言机解决RWA数据孤岛', en: 'Chainlink Founded — Solving RWA\'s Data Problem', detail: { zh: 'Sergey Nazarov 联合创立Chainlink，首次提出去中心化预言机概念，解决区块链无法自动获取现实世界数据的问题。这是RWA核心基础设施的诞生——RWA资产需要知道自己代表的现实资产的实时价值。', en: 'Sergey Nazarov co-founded Chainlink, introducing decentralized oracles — the solution to blockchain\'s data blind spot. Critical for RWA: tokenized assets need real-time data about the assets they represent.' }, tags: ['Chainlink', 'Sergey Nazarov', '预言机'], milestone: true },
  { year: '2017', zh: 'ICO热潮与监管觉醒', en: 'ICO Boom & Regulatory Awakening', detail: { zh: '大量项目以"代币代表真实资产"为噱头发行ICO。各国监管机构陆续介入，SEC发表"DAO报告"，首次明确代币可能属于证券，全球监管框架开始萌芽。ICO泡沫反推行业思考"必须锚定实体资产"。', en: 'Thousands of ICOs exploited "token = real asset" narrative. SEC\'s "DAO Report" first classified certain tokens as securities. The bubble\'s collapse pushed the industry toward genuine asset-backing — accelerating RWA.' }, tags: ['ICO', 'SEC DAO报告', '监管觉醒'], milestone: false },
  { year: '2019', zh: 'Centrifuge诞生，RWA雏形出现', en: 'Centrifuge Launches — Early RWA Prototype', detail: { zh: 'Centrifuge 开发Tinlake协议，首次将真实世界应收账款上链融资，为DeFi引入真实收益来源。被视为现代RWA的雏形项目。同年，新加坡MAS推出数字资产沙盒，亚洲RWA试点正式开始。', en: 'Centrifuge developed the Tinlake protocol, tokenizing real-world receivables for DeFi. First modern RWA prototype. Same year, Singapore MAS launched its digital asset sandbox — Asia\'s first RWA pilot.' }, tags: ['Centrifuge', 'Tinlake', 'MAS沙盒'], milestone: false },
  { year: '2021', zh: 'MakerDAO引入RWA抵押品 + NFT出圈', en: 'MakerDAO Adopts RWA Collateral + NFT Explosion', detail: { zh: 'MakerDAO DAO投票通过，允许真实世界资产（房产贷款、发票）作为DAI的抵押品，标志头部DeFi协议对RWA的官方接纳。同年NFT大规模出圈，将数字艺术品、版权、门票等无形资产代币化，扩展了RWA的范畴。', en: 'MakerDAO voted to accept real-world assets (mortgage loans, invoices) as DAI collateral. Mainstream DeFi now officially accepted RWA. NFT explosion also extended RWA into intangible assets.' }, tags: ['MakerDAO', 'DAI', 'NFT扩展RWA'], milestone: true },
  { year: '2022', zh: '机构级RWA试点全面启动', en: 'Institutional-Grade RWA Pilots Launch Globally', detail: { zh: '新加坡MAS与JP摩根、DBS、星展银行等机构联合启动Project Guardian；摩根大通推出JPM Coin代币化存款；欧洲投资银行发行1亿欧元数字原生债券；以太坊合并完成，能耗降99.9%，满足机构ESG要求。', en: 'Singapore MAS + JPMorgan launched Project Guardian; JPMorgan launched JPM Coin; EIB issued €100M digital native bonds; Ethereum\'s Merge cut energy use 99.9% — satisfying institutional ESG requirements.' }, tags: ['Project Guardian', 'JPM Coin', '以太坊合并'], milestone: true },
  { year: '2023', zh: 'RWA进入爆发前夜', en: 'RWA on the Eve of Explosion', detail: { zh: '贝莱德启动BUIDL基金，富兰克林邓普顿Benji基金突破3亿美元，Ondo Finance国债代币化受机构热捧。全球RWA链上资产首次突破50亿美元。香港VASP发牌制度正式生效，HashKey、OSL获得牌照。', en: 'BlackRock launched BUIDL; Franklin Templeton\'s Benji exceeded $300M; global on-chain RWA surpassed $5B for the first time. Hong Kong VASP licensing took effect — HashKey and OSL licensed.' }, tags: ['贝莱德BUIDL', '香港VASP', '规模突破50亿'], milestone: true },
  { year: '2024', zh: 'RWA全面爆发，全球监管成型', en: 'RWA Full Explosion, Global Regulation Formed', detail: { zh: '全球RWA市场规模突破200亿美元。欧盟MiCA条例12月全面生效，首个超大型经济体统一监管框架。香港HKMA启动Project Ensemble沙盒。贝莱德BUIDL规模突破10亿美元。泰国财政部发行全球首支公开代币化政府债券G-Token。', en: 'Global RWA exceeded $20B. EU MiCA fully effective (December). HKMA launched Project Ensemble. BlackRock BUIDL exceeded $1B. Thailand issued the world\'s first publicly offered tokenized government bond (G-Token).' }, tags: ['MiCA', 'Project Ensemble', 'G-Token泰国主权RWA'], milestone: true },
  { year: '2025', zh: '亚太竞争加速，中东入场，稳定币立法', en: 'Asia-Pacific Race Intensifies, Middle East Enters, Stablecoin Laws Pass', detail: { zh: '日本FSA发布RWA监管指引，UAE引入ADGM数字资产框架，中东主权基金开始布局RWA。香港《稳定币条例》生效，全球首个法币稳定币综合监管框架。全球RWA管理规模突破300亿美元。', en: 'Japan FSA issued RWA guidelines; UAE introduced ADGM framework; Middle East sovereign funds entered RWA. Hong Kong\'s Stablecoin Ordinance took effect (world\'s first). Global RWA AUM exceeded $30B.' }, tags: ['日本监管', 'UAE入场', '香港稳定币条例'], milestone: false },
  { year: '2026', zh: 'RWA元年——合规与规模的双重爆发', en: 'RWA Year One — Compliance & Scale Dual Explosion', detail: { zh: '中国八部委发布全球首个跨境RWA监管框架，香港EnsembleTX法定结算平台强制运营（毫秒级DvP原子结算）。RWA 3.0时代开启：美股代币化、ETF代币化成为核心方向，传统资本市场核心资产大规模上链，预计突破1000亿美元。', en: 'China\'s 8 ministries issued the world\'s first cross-border RWA regulatory framework. HK\'s EnsembleTX mandatory settlement platform launched (millisecond DvP). RWA 3.0: tokenized stocks and ETFs. Market projected to hit $100B.' }, tags: ['八部委框架', 'EnsembleTX', 'RWA 3.0', 'RWA元年'], milestone: true },
];

const chinaTimeline = [
  { year: '2016', zh: '国家级联盟链基础设施起步', en: 'National Consortium Chain Infrastructure Starts', detail: { zh: 'BSN（区块链服务网络）筹备启动，由国家信息中心牵头建设，提供合规的联盟链基础设施。趣链科技、蚂蚁链等国内联盟链企业陆续成立，无币区块链方向逐渐明朗。', en: 'BSN (Blockchain Service Network) began development under the State Information Center, providing compliant consortium chain infrastructure. Qulian, AntChain, and other domestic players emerged.' }, tags: ['BSN', '联盟链', '无币区块链'], milestone: false },
  { year: '2017', zh: '国内全面禁止ICO', en: 'China Bans ICOs Nationwide', detail: { zh: '2017年9月，中国人民银行等七部委联合发布《关于防范代币发行融资风险的公告》，全面叫停ICO，关闭境内虚拟货币交易所，为后续"无币区块链"赛道的合规发展清除干扰。', en: 'September 2017: Seven ministries including PBOC jointly banned all ICOs and closed domestic crypto exchanges — clearing the path for "tokenless blockchain" compliant development.' }, tags: ['网信办备案', '联盟链合法化', 'Conflux'], milestone: false },
  { year: '2021', zh: '全面禁令：境内虚拟货币归零', en: 'Comprehensive Ban: All Domestic Crypto Activities Prohibited', detail: { zh: '国家发改委等十部委联合发布"9.24通知"，全面禁止虚拟货币交易、挖矿、ICO，明确区块链技术应服务实体经济。这一政策切断了虚拟币炒作，引导行业聚焦RWA合规发展。', en: 'Ten ministries issued the "9.24 Notice," banning all domestic crypto activities. Blockchain must serve the real economy. This policy cut off speculation and directed the industry toward compliant RWA.' }, tags: ['九二四通知', '十部委', '全面禁令'], milestone: true },
  { year: '2022', zh: '香港开放路线图发布', en: 'Hong Kong Releases Open Roadmap', detail: { zh: '香港财经事务及库务局发布《有关香港虚拟资产发展的政策宣言》，明确香港走开放监管路线。这为内地机构通过香港合规渠道参与RWA打开了政策窗口，"境内确权+香港发行"模式初现。', en: 'Hong Kong FSIT released its Virtual Asset Policy Statement, firmly establishing an open regulatory path — opening the policy window for the "domestic title + HK issuance" model.' }, tags: ['香港开放', '政策宣言', '窗口期'], milestone: false },
  { year: '2023', zh: '香港VASP发牌制度正式生效', en: 'HK VASP Licensing Regime Takes Effect', detail: { zh: '2023年6月，《虚拟资产服务提供商条例》正式生效，HashKey、OSL率先获得合规牌照。内地机构可通过香港持牌机构，以"境内确权→香港SPV→持牌发行"路径合规参与RWA，跨境RWA通道正式打通。', en: 'June 2023: VASP licensing ordinance took effect. HashKey and OSL licensed first. The path "mainland title → HK SPV → licensed issuance" officially opened.' }, tags: ['VASP牌照', 'HashKey', 'OSL', '跨境RWA'], milestone: true },
  { year: '2024', zh: '香港Project Ensemble沙盒启动 + 稳定币草案', en: 'HK Project Ensemble Sandbox + Stablecoin Draft', detail: { zh: '香港金管局HKMA推出Project Ensemble批发式CBDC沙盒，测试代币化存款与RWA结算，汇丰、渣打、中银等多家银行参与。稳定币法案草案提交立法会，为RWA结算货币合规化奠定基础。', en: 'HKMA launched Project Ensemble wholesale CBDC sandbox. HSBC, Standard Chartered, Bank of China participated in testing RWA settlement. Stablecoin bill draft submitted to LegCo.' }, tags: ['Project Ensemble', 'HKMA', '稳定币草案'], milestone: false },
  { year: '2025', zh: '内地合规框架逐步明朗，国企入场', en: 'Mainland Compliance Framework Clarifies, SOEs Enter', detail: { zh: '证监会对境内企业通过香港SPV发行RWA的备案要求逐步清晰。蚂蚁数科1400万台新能源设备上链，成为全球最大新能源RWA平台。多个省市央国企开始探索将不动产、基础设施资产通过香港合规通道代币化。', en: 'CSRC clarified filing requirements for mainland RWA issuance via HK SPVs. AntChain onboarded 14 million energy devices. Multiple SOEs began tokenizing real estate and infrastructure through HK channels.' }, tags: ['国企入场', '蚂蚁新能源RWA', '证监会备案'], milestone: false },
  { year: '2026', zh: '2026 RWA元年：全球首个跨境监管框架发布', en: '2026 RWA Year One: World\'s First Cross-Border RWA Framework', detail: { zh: '2026年2月6日，中国八部委发布《境内资产境外发行RWA监管框架（试行）》，全球首个专门针对RWA的跨境监管框架。同日，证监会发布备案制，香港EnsembleTX法定结算平台强制运营。优先试点：新能源、供应链、文旅、碳资产四大赛道。', en: 'February 6, 2026: China\'s 8 ministries issued the world\'s first cross-border RWA regulatory framework. Same day: CSRC filing system launched; HK EnsembleTX mandatory settlement platform went live. Priority pilots: clean energy, supply chain, tourism/culture, carbon assets.' }, tags: ['八部委12号文', 'EnsembleTX', '四大赛道', '全球首个框架'], milestone: true },
];

const globalTimeline = [
  { country: '美国', countryEn: 'USA', year: '2020', zh: 'OCC明确银行可持有数字资产', en: 'OCC: Banks May Hold Digital Assets', detail: { zh: 'Brian Brooks主导下，美国货币监理署（OCC）发布解释函，明确银行可合规持有、托管数字资产，包括RWA代币，为传统金融机构参与RWA扫清监管障碍。此后华尔街巨头陆续入局。', en: 'Under Brian Brooks, OCC issued interpretive letters clarifying banks may hold and custody digital assets, including RWA tokens — clearing the path for Wall Street to enter RWA.' }, tags: ['OCC', 'Brian Brooks', '银行合规'], milestone: true, color: '#0EA5E9' },
  { country: '美国', countryEn: 'USA', year: '2024', zh: '贝莱德BUIDL基金上线', en: 'BlackRock BUIDL Fund Launches', detail: { zh: '全球最大资管公司贝莱德发行首支代币化货币市场基金BUIDL，规模破5亿美元，投资代币化美债。机构级RWA正式进入主流投资市场，引发全球资管公司跟风，摩根大通、富兰克林邓普顿等相继发行RWA产品。', en: 'BlackRock launched BUIDL, the world\'s largest tokenized money market fund, exceeding $500M in days. Triggered a wave of institutional RWA product launches globally.' }, tags: ['贝莱德', 'BUIDL', '代币化美债'], milestone: true, color: '#0EA5E9' },
  { country: '欧盟', countryEn: 'EU', year: '2024', zh: 'MiCA法案全面生效', en: 'MiCA Regulation Fully Effective', detail: { zh: '欧盟MiCA条例12月全面生效，全球首个统一的加密资产监管框架，明确区分"资产参考代币（ART）"（即RWA）与其他代币，规定发行、交易、托管的合规要求，为欧盟范围内RWA提供统一法律确定性。', en: 'EU MiCA Regulation fully effective (December). The world\'s first unified crypto asset regulatory framework, explicitly defining "Asset-Referenced Tokens (ART)" (i.e., RWA) and their compliance requirements across all 27 EU member states.' }, tags: ['MiCA', 'ART资产参考代币', '27国统一'], milestone: true, color: '#6366F1' },
  { country: '新加坡', countryEn: 'Singapore', year: '2022', zh: 'Project Guardian跨机构试点', en: 'Project Guardian Cross-Institution Pilots', detail: { zh: 'MAS联合星展银行、摩根大通、淡马锡等24家机构推出Project Guardian，聚焦债券、外汇与基金管理的代币化标准化与互操作性，探索跨境RWA协作模式，成为全球最具影响力的RWA行业协作计划。', en: 'MAS + DBS, JPMorgan, Temasek, and 24 other institutions launched Project Guardian — the world\'s most influential RWA industry collaboration, testing bond/FX/fund tokenization standards.' }, tags: ['Project Guardian', 'MAS', '24家机构'], milestone: true, color: '#10B981' },
  { country: '香港', countryEn: 'Hong Kong', year: '2026', zh: 'EnsembleTX法定结算平台——全球首创', en: 'EnsembleTX Settlement Platform — World First', detail: { zh: 'HKMA推出全球首个强制性代币化资产结算平台EnsembleTX，要求所有持牌机构发行的RWA必须通过该平台实现DvP（券款对付）原子结算，结算时间从T+2缩短至毫秒级，彻底解决RWA跨境结算效率低的痛点。', en: 'HKMA launched EnsembleTX — the world\'s first mandatory tokenized asset settlement platform. All licensed institution RWA must settle here using DvP (Delivery vs. Payment) atomic settlement: T+2 reduced to milliseconds.' }, tags: ['EnsembleTX', 'DvP原子结算', '毫秒级', '全球首创'], milestone: true, color: '#F59E0B' },
  { country: '泰国', countryEn: 'Thailand', year: '2025', zh: 'G-Token——全球首支主权RWA', en: 'G-Token — World\'s First Sovereign RWA', detail: { zh: '泰国财政部发行全球首支公开发行的代币化政府债券G-Token，规模50亿泰铢（约1.53亿美元），期限2年，票面利率1.8%，面向全球投资者发行，支持链上付息与赎回，验证了RWA在政府融资中的可行性。', en: 'Thailand Finance Ministry issued G-Token — the world\'s first publicly offered tokenized government bond: 5B baht (~$153M), 2-year term, 1.8% coupon, global investors, on-chain interest payments and redemptions.' }, tags: ['G-Token', '主权RWA', '泰国财政部'], milestone: true, color: '#EC4899' },
  { country: '日本', countryEn: 'Japan', year: '2024', zh: 'FSA发布RWA监管指引 + 税收优惠', en: 'Japan FSA Issues RWA Guidelines + Tax Benefits', detail: { zh: '日本金融厅（FSA）发布RWA监管指引，允许银行和证券公司开展代币化证券业务。同期推出RWA持有税收优惠（持有1年以上免资本利得税），三菱UFJ银行率先推出代币化贷款产品，面向零售与机构投资者。', en: 'Japan FSA issued RWA guidelines allowing banks and securities firms to engage in tokenized securities. Tax incentives introduced (1-year+ holdings exempt from capital gains). MUFG launched tokenized loan products.' }, tags: ['日本FSA', '税收优惠', '三菱UFJ'], milestone: false, color: '#DC2626' },
  { country: '阿联酋', countryEn: 'UAE', year: '2023', zh: 'VARA牌照制度+迪拜RWA生态', en: 'VARA License System + Dubai RWA Ecosystem', detail: { zh: '阿联酋虚拟资产监管局（VARA）发布RWA牌照制度，ADGM（阿布扎比全球市场）和DIFC（迪拜国际金融中心）分别建立RWA监管沙盒。提供税收优惠与落地补贴，吸引全球RWA资本与项目落地，重点发展房地产、能源、家族办公室资产RWA。', en: 'UAE\'s VARA introduced RWA licensing. ADGM and DIFC established separate sandboxes. Tax incentives and subsidies attracted global RWA capital — focusing on real estate, energy, and family office assets.' }, tags: ['VARA', 'ADGM', 'DIFC', '迪拜'], milestone: false, color: '#F97316' },
  { country: '英国', countryEn: 'UK', year: '2025', zh: 'FCA数字证券沙盒 + 英国国债代币化', en: 'FCA Digital Securities Sandbox + UK Gilt Tokenization', detail: { zh: '英国FCA扩大数字证券监管沙盒，支持更多RWA项目试点。2025年启动代币化政府债券（gilts）试点，伦敦证券交易所将3只富时100指数ETF代币化，支持24小时交易，成为英国首个主流金融产品RWA。', en: 'FCA expanded digital securities sandbox. UK launched tokenized gilt (government bond) pilots. LSE tokenized 3 FTSE 100 ETFs for 24-hour trading — UK\'s first mainstream financial product RWA.' }, tags: ['英国FCA', '英国国债代币化', 'LSE ETF'], milestone: false, color: '#7C3AED' },
];

// ── Find the index of the closest item to current year ──
function findCurrentYearIndex(data: { year: string }[]): number {
  const currentYear = new Date().getFullYear();
  let closestIdx = 0;
  let minDiff = Infinity;
  data.forEach((item, idx) => {
    const diff = Math.abs(parseInt(item.year, 10) - currentYear);
    if (diff < minDiff) {
      minDiff = diff;
      closestIdx = idx;
    }
  });
  return closestIdx;
}

// ── Virtual Timeline Item Component ──
interface TimelineItemProps {
  item: typeof intlTimeline[0];
  index: number;
  t: (zh: string, en: string) => string;
  isCurrentYear?: boolean;
}

function TimelineItem({ item, index, t, isCurrentYear = false }: TimelineItemProps) {
  const isLeft = index % 2 === 0;
  const currentYear = new Date().getFullYear().toString();
  const isFuture = parseInt(item.year, 10) > new Date().getFullYear();

  return (
    <div className={`relative flex gap-8 ${isLeft ? 'sm:flex-row' : 'sm:flex-row-reverse'} flex-col`}>
      <div className="flex-1 sm:max-w-[calc(50%-2.5rem)]">
        <div
          className={`relative border rounded-2xl p-6 transition-all ${
            isCurrentYear
              ? 'border-[#10B981]/60 bg-[#10B981]/8'
              : item.milestone
              ? 'border-[#F59E0B]/40 bg-[#F59E0B]/5 bg-white/5'
              : 'border-white/10 bg-white/5 hover:bg-white/10'
          }`}
          style={
            isCurrentYear
              ? { boxShadow: '0 0 0 2px rgba(16,185,129,0.35), 0 0 24px rgba(16,185,129,0.15)' }
              : undefined
          }
        >
          {/* Current year badge */}
          {isCurrentYear && (
            <div className="absolute -top-2.5 left-4 flex items-center gap-1.5">
              <span className="px-2.5 py-0.5 bg-[#10B981] text-white text-xs font-black rounded-full flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse inline-block"></span>
                {t('当前年份', 'Current Year')}
              </span>
            </div>
          )}
          {/* Future badge */}
          {isFuture && !isCurrentYear && (
            <div className="absolute -top-2 right-4">
              <span className="px-2 py-0.5 bg-[#7C3AED]/80 text-white text-xs font-bold rounded-full">
                {t('预测', 'Forecast')}
              </span>
            </div>
          )}
          {item.milestone && !isCurrentYear && (
            <div className="absolute -top-2 right-4">
              <span className="px-2 py-0.5 bg-[#F59E0B] text-[#1A0050] text-xs font-black rounded-full">{t('关键节点', 'Key Milestone')}</span>
            </div>
          )}
          <div className="flex items-center gap-3 mb-3">
            <span
              className="text-2xl font-black font-mono"
              style={{
                color: isCurrentYear ? '#10B981' : item.milestone ? '#F59E0B' : '#A78BFA',
              }}
            >
              {item.year}
            </span>
            <h4 className="text-white font-bold text-sm">{t(item.zh, item.en)}</h4>
          </div>
          <p className="text-white/60 text-xs leading-relaxed mb-3">{t(item.detail.zh, item.detail.en)}</p>
          <div className="flex flex-wrap gap-1.5">
            {item.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 rounded-full"
                style={{
                  background: isCurrentYear ? 'rgba(16,185,129,0.15)' : 'rgba(167,139,250,0.15)',
                  color: isCurrentYear ? '#6EE7B7' : '#C4B5FD',
                  border: isCurrentYear ? '1px solid rgba(16,185,129,0.2)' : '1px solid rgba(167,139,250,0.2)',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="hidden sm:flex flex-col items-center justify-start pt-6 shrink-0">
        <div
          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
            isCurrentYear
              ? 'bg-[#10B981] border-[#10B981] shadow-lg shadow-[#10B981]/50'
              : item.milestone
              ? 'bg-[#F59E0B] border-[#F59E0B] shadow-lg shadow-[#F59E0B]/50'
              : 'bg-[#7C3AED] border-[#7C3AED]'
          }`}
          style={isCurrentYear ? { animation: 'ping-slow 2s ease-in-out infinite' } : undefined}
        >
          {(item.milestone || isCurrentYear) && <div className="w-2 h-2 bg-[#1A0050] rounded-full" />}
        </div>
      </div>
      <div className="flex-1 sm:max-w-[calc(50%-2.5rem)] hidden sm:block" />
    </div>
  );
}

// ── Virtual Timeline List Component ──
interface VirtualTimelineListProps {
  data: typeof intlTimeline;
  t: (zh: string, en: string) => string;
}

function VirtualTimelineList({ data, t }: VirtualTimelineListProps) {
  const { containerRef, progress, isScrollable, isAtBottom } = useScrollProgress();
  const currentYearIdx = useMemo(() => findCurrentYearIndex(data), [data]);
  const hasScrolledRef = useRef(false);

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 180,
    overscan: 3,
  });

  // Auto-scroll to current year node on first mount
  useEffect(() => {
    if (hasScrolledRef.current) return;
    const el = containerRef.current;
    if (!el) return;

    // Small delay to let virtualizer measure
    const timer = setTimeout(() => {
      virtualizer.scrollToIndex(currentYearIdx, { align: 'center', behavior: 'smooth' });
      hasScrolledRef.current = true;
    }, 400);
    return () => clearTimeout(timer);
  }, [currentYearIdx, virtualizer]);

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className="relative">
      {/* Current year jump button */}
      <div className="mb-3 flex items-center gap-3">
        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
          {isScrollable && (
            <div
              className="h-full rounded-full transition-all duration-150"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #A78BFA, #F59E0B)',
              }}
            />
          )}
        </div>
        <button
          onClick={() => {
            virtualizer.scrollToIndex(currentYearIdx, { align: 'center', behavior: 'smooth' });
          }}
          className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold cursor-pointer whitespace-nowrap transition-all hover:scale-105"
          style={{ background: 'rgba(16,185,129,0.2)', color: '#6EE7B7', border: '1px solid rgba(16,185,129,0.3)' }}
        >
          <span className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-pulse inline-block"></span>
          {t('跳到当前年份', 'Jump to Now')} · {data[currentYearIdx]?.year}
        </button>
        {isScrollable && (
          <span className="text-white/40 text-xs whitespace-nowrap shrink-0">
            {isAtBottom
              ? t('已浏览全部', 'All viewed')
              : `${progress}%`}
          </span>
        )}
      </div>

      <div
        ref={containerRef}
        className="relative h-[600px] overflow-auto"
        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(167,139,250,0.3) transparent' }}
      >
        <div className="relative" style={{ height: `${virtualizer.getTotalSize()}px` }}>
          <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#7C3AED]/80 via-[#F59E0B]/60 to-[#7C3AED]/20 hidden sm:block" />
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
            }}
          >
            {virtualItems.map((virtualItem) => (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                className="py-4"
              >
                <TimelineItem
                  item={data[virtualItem.index]}
                  index={virtualItem.index}
                  t={t}
                  isCurrentYear={virtualItem.index === currentYearIdx}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom fade hint */}
      {isScrollable && !isAtBottom && (
        <div className="relative -mt-12 h-12 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, rgba(26,0,80,0.85))' }}>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 text-white/40 text-xs">
            <i className="ri-arrow-down-s-line animate-bounce"></i>
            <span>{t('继续滚动', 'Scroll down')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Virtual Global Timeline List ──
interface VirtualGlobalListProps {
  data: typeof globalTimeline;
  t: (zh: string, en: string) => string;
}

function VirtualGlobalList({ data, t }: VirtualGlobalListProps) {
  const { containerRef, progress, isScrollable, isAtBottom } = useScrollProgress();
  
  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 160,
    overscan: 2,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className="relative">
      {/* Progress indicator */}
      {isScrollable && (
        <div className="mb-3 flex items-center gap-3">
          <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-150"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #10B981, #F59E0B)',
              }}
            />
          </div>
          <span className="text-white/40 text-xs whitespace-nowrap shrink-0">
            {isAtBottom
              ? t('已浏览全部', 'All viewed')
              : `${progress}% · ${t('向下滚动查看更多', 'Scroll for more')}`}
          </span>
        </div>
      )}

      <div
        ref={containerRef}
        className="relative h-[600px] overflow-auto space-y-6"
        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(167,139,250,0.3) transparent' }}
      >
        <div className="relative" style={{ height: `${virtualizer.getTotalSize()}px` }}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
            }}
          >
            {virtualItems.map((virtualItem) => (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                className="py-3"
              >
                <GlobalTimelineItem item={data[virtualItem.index]} t={t} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom fade hint */}
      {isScrollable && !isAtBottom && (
        <div className="relative -mt-12 h-12 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, rgba(26,0,80,0.85))' }}>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 text-white/40 text-xs">
            <i className="ri-arrow-down-s-line animate-bounce"></i>
            <span>{t('继续滚动', 'Scroll down')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Global Timeline Item Component ──
interface GlobalTimelineItemProps {
  item: typeof globalTimeline[0];
  t: (zh: string, en: string) => string;
}

function GlobalTimelineItem({ item, t }: GlobalTimelineItemProps) {
  return (
    <div className="relative bg-white/5 border rounded-2xl p-6 hover:bg-white/8 transition-all" style={{ borderColor: `${item.color}40` }}>
      {item.milestone && (
        <div className="absolute -top-2 right-4">
          <span className="px-2 py-0.5 text-white text-xs font-black rounded-full" style={{ background: item.color }}>{t('全球里程碑', 'Global Milestone')}</span>
        </div>
      )}
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0 text-white text-sm font-black" style={{ background: `${item.color}30`, border: `1px solid ${item.color}40` }}>
          {item.year}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-xs px-2 py-0.5 rounded-full font-bold text-white" style={{ background: item.color }}>
              {t(item.country, item.countryEn)}
            </span>
            <h4 className="text-white font-bold text-sm">{t(item.zh, item.en)}</h4>
          </div>
          <p className="text-white/60 text-xs leading-relaxed mb-3">{t(item.detail.zh, item.detail.en)}</p>
          <div className="flex flex-wrap gap-1.5">
            {item.tags.map((tag) => (
              <span key={tag} className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${item.color}15`, color: item.color, border: `1px solid ${item.color}25` }}>{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RWATimeline() {
  const [tab, setTab] = useState<'pre' | 'intl' | 'china' | 'global'>('intl');
  const [milestoneOnly, setMilestoneOnly] = useState(false);
  const [yearRange, setYearRange] = useState<[number, number] | null>(null);
  const { t } = useLang();

  // Compute min/max years for current tab
  const tabYearBounds = useMemo(() => {
    let base: { year: string }[];
    switch (tab) {
      case 'pre': base = preBlockchainTimeline; break;
      case 'intl': base = intlTimeline; break;
      case 'china': base = chinaTimeline; break;
      case 'global': base = globalTimeline; break;
      default: base = intlTimeline;
    }
    const years = base.map((i) => parseInt(i.year, 10));
    return { min: Math.min(...years), max: Math.max(...years) };
  }, [tab]);

  // Reset year range when tab changes
  useEffect(() => {
    setYearRange(null);
  }, [tab]);

  const effectiveRange: [number, number] = yearRange ?? [tabYearBounds.min, tabYearBounds.max];

  // Memoize current data based on tab + milestone filter + year range
  const currentData = useMemo(() => {
    let base: typeof intlTimeline;
    switch (tab) {
      case 'pre': base = preBlockchainTimeline; break;
      case 'intl': base = intlTimeline; break;
      case 'china': base = chinaTimeline; break;
      case 'global': base = globalTimeline; break;
      default: base = intlTimeline;
    }
    let filtered = milestoneOnly ? base.filter((item) => item.milestone) : base;
    // Apply year range filter
    filtered = filtered.filter((item) => {
      const y = parseInt(item.year, 10);
      return y >= effectiveRange[0] && y <= effectiveRange[1];
    });
    return filtered;
  }, [tab, milestoneOnly, effectiveRange]);

  const totalMilestones = useMemo(() => {
    switch (tab) {
      case 'pre': return preBlockchainTimeline.filter((i) => i.milestone).length;
      case 'intl': return intlTimeline.filter((i) => i.milestone).length;
      case 'china': return chinaTimeline.filter((i) => i.milestone).length;
      case 'global': return globalTimeline.filter((i) => i.milestone).length;
      default: return 0;
    }
  }, [tab]);

  const isYearFiltered = yearRange !== null && (yearRange[0] !== tabYearBounds.min || yearRange[1] !== tabYearBounds.max);

  return (
    <section id="rwa-timeline" className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #2E1065 0%, #1A0050 100%)' }}>
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(#A78BFA 1px, transparent 1px), linear-gradient(90deg, #A78BFA 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
      <div className="relative z-10 max-w-[1440px] mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#F59E0B]/15 border border-[#F59E0B]/30 rounded-full mb-4">
            <i className="ri-history-line text-[#F59E0B] text-sm"></i>
            <span className="text-[#F59E0B] text-sm font-semibold">{t('RWA发展历程', 'RWA Development History')}</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
            {t('区块链与RWA ', 'Blockchain & RWA ')}
            <span className="text-[#A78BFA]">{t('完整演进脉络', 'Complete Evolution Timeline')}</span>
          </h2>
          <p className="text-white/55 max-w-xl mx-auto text-base">
            {t(
              '从1976年密码学奠基到2026年RWA元年，梳理全球区块链技术与RWA产业的完整历程',
              'From 1976 cryptography to the 2026 RWA era — the complete history of global blockchain technology and the RWA industry'
            )}
          </p>
        </div>

        {/* 4-tab Switcher */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex flex-wrap justify-center bg-white/5 border border-white/10 rounded-2xl p-1 gap-1">
            <button onClick={() => setTab('pre')} className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${tab === 'pre' ? 'bg-gradient-to-r from-[#6D28D9] to-[#4C1D95] text-white shadow-lg' : 'text-white/55 hover:text-white'}`}>
              🔬 {t('技术思想萌芽', 'Pre-Blockchain Era')} <span className="text-xs opacity-70">(1976-2008)</span>
            </button>
            <button onClick={() => setTab('intl')} className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${tab === 'intl' ? 'bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] text-white shadow-lg' : 'text-white/55 hover:text-white'}`}>
              🌐 {t('国际发展历程', 'International History')}
            </button>
            <button onClick={() => setTab('china')} className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${tab === 'china' ? 'bg-gradient-to-r from-[#F59E0B] to-[#FB923C] text-[#1A0050] shadow-lg' : 'text-white/55 hover:text-white'}`}>
              🇨🇳🇭🇰 {t('中国/香港历程', 'China / HK History')}
            </button>
            <button onClick={() => setTab('global')} className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${tab === 'global' ? 'bg-gradient-to-r from-[#10B981] to-[#059669] text-white shadow-lg' : 'text-white/55 hover:text-white'}`}>
              🗺️ {t('全球各国历程', 'Global Country Map')}
            </button>
          </div>
        </div>

        {/* ── Year Range Filter ── */}
        <YearRangeFilter
          min={tabYearBounds.min}
          max={tabYearBounds.max}
          value={effectiveRange}
          onChange={(range) => setYearRange(range)}
          onReset={() => setYearRange(null)}
          isFiltered={isYearFiltered}
          filteredCount={currentData.length}
          t={t}
        />

        {/* ── Milestone Filter Toggle ── */}
        <div className="flex justify-center mb-10">
          <div className="flex items-center gap-3 px-4 py-2.5 rounded-2xl border" style={{ background: 'rgba(255,255,255,0.04)', borderColor: milestoneOnly ? 'rgba(245,158,11,0.5)' : 'rgba(255,255,255,0.1)' }}>
            <button
              onClick={() => setMilestoneOnly(false)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${!milestoneOnly ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/70'}`}
            >
              <i className="ri-list-check text-sm"></i>
              {t('全部节点', 'All Events')}
              <span className="text-white/40 font-normal">
                ({tab === 'pre' ? preBlockchainTimeline.length : tab === 'intl' ? intlTimeline.length : tab === 'china' ? chinaTimeline.length : globalTimeline.length})
              </span>
            </button>
            <div className="w-px h-4 bg-white/15" />
            <button
              onClick={() => setMilestoneOnly(true)}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${milestoneOnly ? 'bg-[#F59E0B]/20 text-[#F59E0B]' : 'text-white/40 hover:text-white/70'}`}
              style={milestoneOnly ? { border: '1px solid rgba(245,158,11,0.3)' } : undefined}
            >
              <i className="ri-star-line text-sm" style={{ color: milestoneOnly ? '#F59E0B' : undefined }}></i>
              {t('仅看关键里程碑', 'Milestones Only')}
              <span
                className="px-1.5 py-0.5 rounded-full text-xs font-black"
                style={{ background: milestoneOnly ? 'rgba(245,158,11,0.25)' : 'rgba(255,255,255,0.1)', color: milestoneOnly ? '#F59E0B' : 'rgba(255,255,255,0.4)' }}
              >
                {totalMilestones}
              </span>
            </button>
          </div>
        </div>

        {/* Milestone mode hint */}
        {milestoneOnly && (
          <div className="flex justify-center mb-6">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full text-xs" style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', color: 'rgba(245,158,11,0.8)' }}>
              <i className="ri-filter-3-line"></i>
              {t(`已筛选：仅显示 ${totalMilestones} 个关键里程碑节点`, `Filtered: showing ${totalMilestones} key milestone events only`)}
              <button onClick={() => setMilestoneOnly(false)} className="ml-1 hover:text-[#F59E0B] cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
        )}

        {/* No results */}
        {currentData.length === 0 && (
          <div className="text-center py-16 text-white/40">
            <i className="ri-calendar-close-line text-4xl mb-3 block"></i>
            <p className="text-base">{t('该时间范围内暂无节点，请调整年份范围', 'No events in this range. Try adjusting the year filter.')}</p>
          </div>
        )}

        {/* Pre-blockchain tab */}
        {tab === 'pre' && currentData.length > 0 && (
          <div>
            <div className="text-center mb-8">
              <p className="text-white/50 text-sm max-w-2xl mx-auto">
                {t(
                  '1991年时间戳系统诞生到2008年比特币白皮书发布之前，密码学家们的思想积累，为区块链技术的诞生奠定了所有理论基础',
                  'From the 1991 timestamp system to the 2008 Bitcoin whitepaper — how cryptographers spent decades building the intellectual foundations that made blockchain possible'
                )}
              </p>
            </div>
            <VirtualTimelineList data={currentData as typeof intlTimeline} t={t} />
          </div>
        )}

        {/* International timeline */}
        {tab === 'intl' && currentData.length > 0 && <VirtualTimelineList data={currentData as typeof intlTimeline} t={t} />}

        {/* China/HK timeline */}
        {tab === 'china' && currentData.length > 0 && <VirtualTimelineList data={currentData as typeof intlTimeline} t={t} />}

        {/* Global countries */}
        {tab === 'global' && currentData.length > 0 && (
          <div>
            <div className="text-center mb-8">
              <p className="text-white/50 text-sm max-w-2xl mx-auto">
                {t(
                  '全球主要司法管辖区的RWA发展历程、关键监管政策与代表性案例',
                  'RWA development history, key regulatory policies, and representative cases across major global jurisdictions'
                )}
              </p>
            </div>
            <VirtualGlobalList data={currentData as typeof globalTimeline} t={t} />
          </div>
        )}

        <div className="mt-16 text-center">
          <div className="inline-flex flex-col items-center gap-2 px-8 py-5 rounded-2xl" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.25)' }}>
            <div className="text-[#F59E0B] font-black text-xl">{t('2026 = RWA元年', '2026 = RWA Year One')}</div>
            <p className="text-white/60 text-sm max-w-md leading-relaxed">
              {t(
                '对于中国企业而言，这是合规参与RWA窗口期的关键之年。香港持牌通道已完备，落地SOP已清晰，现在正是了解和布局的最佳时机。',
                'For Chinese enterprises, this is the critical compliance window year. Hong Kong licensed channels are ready, the SOP is clear — now is the best time to understand and position.'
              )}
            </p>
            <a href="#landing" className="mt-2 inline-flex items-center gap-2 px-6 py-2.5 font-bold rounded-full text-sm cursor-pointer transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #F59E0B, #FB923C)', color: '#1A0050' }}>
              <i className="ri-route-line"></i>
              {t('查看企业落地SOP', 'View Enterprise SOP')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Year Range Filter Component ──
interface YearRangeFilterProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (range: [number, number]) => void;
  onReset: () => void;
  isFiltered: boolean;
  filteredCount: number;
  t: (zh: string, en: string) => string;
}

function YearRangeFilter({ min, max, value, onChange, onReset, isFiltered, filteredCount, t }: YearRangeFilterProps) {
  const [localMin, setLocalMin] = useState(value[0]);
  const [localMax, setLocalMax] = useState(value[1]);

  // Sync when external value changes (e.g. tab switch resets)
  useEffect(() => {
    setLocalMin(value[0]);
    setLocalMax(value[1]);
  }, [value[0], value[1]]);

  const totalYears = max - min;
  const leftPct = totalYears > 0 ? ((localMin - min) / totalYears) * 100 : 0;
  const rightPct = totalYears > 0 ? ((localMax - min) / totalYears) * 100 : 100;

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Math.min(parseInt(e.target.value, 10), localMax - 1);
    setLocalMin(v);
    onChange([v, localMax]);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Math.max(parseInt(e.target.value, 10), localMin + 1);
    setLocalMax(v);
    onChange([localMin, v]);
  };

  return (
    <div className="flex justify-center mb-6">
      <div
        className="w-full max-w-2xl px-5 py-4 rounded-2xl border"
        style={{ background: 'rgba(255,255,255,0.04)', borderColor: isFiltered ? 'rgba(167,139,250,0.5)' : 'rgba(255,255,255,0.1)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <i className="ri-calendar-2-line text-[#A78BFA] text-sm"></i>
            <span className="text-white/70 text-xs font-bold">{t('年份范围筛选', 'Year Range Filter')}</span>
            {isFiltered && (
              <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: 'rgba(167,139,250,0.2)', color: '#C4B5FD' }}>
                {localMin} – {localMax}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-white/40 text-xs">
              {t(`共 ${filteredCount} 个节点`, `${filteredCount} events`)}
            </span>
            {isFiltered && (
              <button
                onClick={onReset}
                className="flex items-center gap-1 text-xs cursor-pointer transition-colors hover:text-white/80 whitespace-nowrap"
                style={{ color: '#A78BFA' }}
              >
                <i className="ri-refresh-line text-xs"></i>
                {t('重置', 'Reset')}
              </button>
            )}
          </div>
        </div>

        {/* Slider track */}
        <div className="relative h-6 flex items-center">
          {/* Background track */}
          <div className="absolute inset-x-0 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }} />
          {/* Active range track */}
          <div
            className="absolute h-1.5 rounded-full"
            style={{
              left: `${leftPct}%`,
              right: `${100 - rightPct}%`,
              background: 'linear-gradient(90deg, #A78BFA, #F59E0B)',
            }}
          />
          {/* Min thumb */}
          <input
            type="range"
            min={min}
            max={max}
            value={localMin}
            onChange={handleMinChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            style={{ zIndex: localMin > max - 5 ? 5 : 3 }}
          />
          {/* Max thumb */}
          <input
            type="range"
            min={min}
            max={max}
            value={localMax}
            onChange={handleMaxChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            style={{ zIndex: 4 }}
          />
          {/* Visual min thumb */}
          <div
            className="absolute w-4 h-4 rounded-full border-2 border-[#A78BFA] bg-[#1A0050] pointer-events-none transition-all"
            style={{ left: `calc(${leftPct}% - 8px)`, zIndex: 6 }}
          />
          {/* Visual max thumb */}
          <div
            className="absolute w-4 h-4 rounded-full border-2 border-[#F59E0B] bg-[#1A0050] pointer-events-none transition-all"
            style={{ left: `calc(${rightPct}% - 8px)`, zIndex: 6 }}
          />
        </div>

        {/* Year labels */}
        <div className="flex justify-between mt-2">
          <span className="text-xs font-mono font-bold" style={{ color: '#A78BFA' }}>{localMin}</span>
          <div className="flex gap-2">
            {/* Quick preset buttons */}
            {[
              { label: t('近10年', 'Last 10yr'), range: [Math.max(min, max - 10), max] as [number, number] },
              { label: t('近5年', 'Last 5yr'), range: [Math.max(min, max - 5), max] as [number, number] },
              { label: t('全部', 'All'), range: [min, max] as [number, number] },
            ].map((preset) => (
              <button
                key={preset.label}
                onClick={() => { setLocalMin(preset.range[0]); setLocalMax(preset.range[1]); onChange(preset.range); }}
                className="text-xs px-2 py-0.5 rounded-full cursor-pointer transition-all hover:scale-105 whitespace-nowrap"
                style={{
                  background: localMin === preset.range[0] && localMax === preset.range[1]
                    ? 'rgba(167,139,250,0.25)'
                    : 'rgba(255,255,255,0.08)',
                  color: localMin === preset.range[0] && localMax === preset.range[1]
                    ? '#C4B5FD'
                    : 'rgba(255,255,255,0.4)',
                  border: localMin === preset.range[0] && localMax === preset.range[1]
                    ? '1px solid rgba(167,139,250,0.3)'
                    : '1px solid transparent',
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <span className="text-xs font-mono font-bold" style={{ color: '#F59E0B' }}>{localMax}</span>
        </div>
      </div>
    </div>
  );
}